/**
 * Search Result Card - Unified card component for search results
 */

import React from 'react';
import { GraduationCap, BookOpen, Globe, MapPin, DollarSign, TrendingUp, Shield, ArrowRight } from 'lucide-react';
import type { SearchResult } from '../types';

interface SearchResultCardProps {
  result: SearchResult;
  onClick: () => void;
}

export const SearchResultCard: React.FC<SearchResultCardProps> = ({ result, onClick }) => {
  const getIcon = () => {
    switch (result.type) {
      case 'university':
        return <GraduationCap className="h-5 w-5 text-success" />;
      case 'program':
        return <BookOpen className="h-5 w-5 text-info" />;
      case 'country':
        return <Globe className="h-5 w-5 text-primary" />;
    }
  };

  const getTypeLabel = () => {
    switch (result.type) {
      case 'university':
        return 'University';
      case 'program':
        return 'Program';
      case 'country':
        return 'Country';
    }
  };

  const getTypeColor = () => {
    switch (result.type) {
      case 'university':
        return 'bg-success/10 text-success';
      case 'program':
        return 'bg-info/10 text-info';
      case 'country':
        return 'bg-primary/10 text-primary';
    }
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return null;
    return `$${(amount / 1000).toFixed(0)}k`;
  };

  return (
    <div className="bg-card rounded-2xl p-6 border-2 border-border hover:shadow-lg hover:border-primary transition-all group">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
              {result.name}
            </h3>
            {result.description && (
              <p className="text-sm text-muted-foreground mt-1">{result.description}</p>
            )}
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0 ml-3 ${getTypeColor()}`}>
          {getTypeLabel()}
        </span>
      </div>

      {/* Type-specific metadata */}
      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
        {/* University metadata */}
        {result.type === 'university' && (
          <>
            {result.metadata.country && (
              <span className="flex items-center gap-1.5">
                {result.metadata.country_flag && <span>{result.metadata.country_flag}</span>}
                <MapPin className="h-3.5 w-3.5" />
                {result.metadata.city ? `${result.metadata.city}, ` : ''}{result.metadata.country}
              </span>
            )}
            {result.metadata.ranking && (
              <span className="px-2 py-1 bg-accent/50 text-accent-foreground rounded text-xs font-semibold">
                QS #{result.metadata.ranking}
              </span>
            )}
            {result.metadata.tuition_min && (
              <span className="flex items-center gap-1">
                <DollarSign className="h-3.5 w-3.5" />
                {formatCurrency(result.metadata.tuition_min)} - {formatCurrency(result.metadata.tuition_max)}
              </span>
            )}
            {result.metadata.programs_count > 0 && (
              <span>{result.metadata.programs_count} programs</span>
            )}
          </>
        )}

        {/* Program metadata */}
        {result.type === 'program' && (
          <>
            {result.metadata.university && (
              <span className="flex items-center gap-1.5">
                <GraduationCap className="h-3.5 w-3.5" />
                {result.metadata.university}
              </span>
            )}
            {result.metadata.country && (
              <span className="flex items-center gap-1.5">
                {result.metadata.country_flag && <span>{result.metadata.country_flag}</span>}
                {result.metadata.country}
              </span>
            )}
            {result.metadata.degree_type && (
              <span className="px-2 py-1 bg-accent/50 text-accent-foreground rounded text-xs font-semibold">
                {result.metadata.degree_type}
              </span>
            )}
            {result.metadata.duration_years && (
              <span>{result.metadata.duration_years} years</span>
            )}
            {result.metadata.tuition && (
              <span className="flex items-center gap-1">
                <DollarSign className="h-3.5 w-3.5" />
                {formatCurrency(result.metadata.tuition)}/year
              </span>
            )}
            {result.metadata.is_online && (
              <span className="px-2 py-1 bg-info/10 text-info rounded text-xs font-medium">
                Online Available
              </span>
            )}
          </>
        )}

        {/* Country metadata */}
        {result.type === 'country' && (
          <>
            {result.metadata.flag_emoji && (
              <span className="text-2xl leading-none">{result.metadata.flag_emoji}</span>
            )}
            {result.metadata.region && (
              <span className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" />
                {result.metadata.region}
              </span>
            )}
            {result.metadata.universities_count && (
              <span className="flex items-center gap-1.5">
                <GraduationCap className="h-3.5 w-3.5" />
                {result.metadata.universities_count} universities
              </span>
            )}
            {result.metadata.safety_index !== null && result.metadata.safety_index !== undefined && (
              <span className="flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5" />
                Safety: {typeof result.metadata.safety_index === 'number' ? result.metadata.safety_index.toFixed(1) : result.metadata.safety_index}/100
              </span>
            )}
            {result.metadata.avg_living_cost && (
              <span className="flex items-center gap-1">
                <DollarSign className="h-3.5 w-3.5" />
                {formatCurrency(result.metadata.avg_living_cost)}/year
              </span>
            )}
            {result.metadata.post_study_visa && result.metadata.post_study_visa > 0 && (
              <span className="px-2 py-1 bg-info/10 text-info rounded text-xs font-medium flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                {result.metadata.post_study_visa}mo Work Visa
              </span>
            )}
          </>
        )}
      </div>

      {/* View Details Button */}
      <div className="mt-4 pt-4 border-t border-border">
        <button
          onClick={onClick}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl transition-colors"
        >
          View Details
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
